import React from "react";
import { Inbox, LayoutGrid, List, Rocket } from "lucide-react";

/** Sigue al cursor para alimentar el halo de `.dyd-card` (--mx / --my). */
export const trackPointer = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

/** Indicador de carga de la casa: anillo bicolor + texto en pulso. */
export function Loader({ text = "Cargando…" }) {
  return (
    <div className="dyd-loader">
      <div className="dyd-loader-ring" />
      <span>{text}</span>
    </div>
  );
}

/** Rejilla de esqueletos mientras llegan los datos. */
export function SkeletonGrid({ count = 6, height = 300 }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 290px), 1fr))",
        gap: "26px",
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="dyd-skeleton dyd-rise"
          style={{ height, "--i": i }}
        />
      ))}
    </div>
  );
}

/** Selector para alternar entre vista de rejilla y de lista. */
export function ViewToggle({ value, onChange }) {
  return (
    <div className="dyd-view-toggle" role="group" aria-label="Cambiar vista">
      <button
        type="button"
        className={value === "grid" ? "is-active" : ""}
        onClick={() => onChange("grid")}
        aria-pressed={value === "grid"}
      >
        <LayoutGrid size={15} />
        Rejilla
      </button>
      <button
        type="button"
        className={value === "list" ? "is-active" : ""}
        onClick={() => onChange("list")}
        aria-pressed={value === "list"}
      >
        <List size={15} />
        Lista
      </button>
    </div>
  );
}

/** Estado vacío con icono, título y pista opcional. */
export function EmptyState({ icon: Icon = Inbox, title, hint }) {
  return (
    <div className="dyd-empty dyd-rise">
      <Icon size={42} strokeWidth={1.3} />
      <h3>{title}</h3>
      {hint && <p>{hint}</p>}
    </div>
  );
}

/** Iniciales del nombre, como mucho dos, para el monograma de la portada. */
const getInitials = (name = "") =>
  name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

/**
 * Portada para lo que no tiene imagen propia: monograma con las iniciales
 * sobre el degradado de marca. Da un acabado sobrio y reconocible en lugar de
 * una imagen genérica repetida en todas las tarjetas.
 *
 * `variant` ajusta el tamaño al sitio donde va: "card" (rejilla), "list"
 * (fila) o "hero" (ficha del proyecto).
 */
export function MediaPlaceholder({ name, variant = "card" }) {
  const initials = getInitials(name);

  return (
    <div
      className={`dyd-media-ph dyd-media-ph-${variant}`}
      role="img"
      aria-label={name ? `${name}, sin imagen` : "Sin imagen"}
    >
      <span className="dyd-media-ph-mark">
        {initials || <Rocket size={variant === "list" ? 20 : 34} strokeWidth={1.5} />}
      </span>
    </div>
  );
}
